import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompareMetric } from '../../models/loyalty-dashboard.model';
import {
  computeDomain,
  formatMetricValue,
  gatherDomainValues,
  tierBandPercents,
  valueToPercent,
  zeroLinePercent,
} from './compare-widget.utils';

@Component({
  selector: 'app-compare-metric-row',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="compare-row">
      <div class="compare-row__top">
        <span class="compare-row__label">{{ metric().label }}</span>
        <span class="compare-row__you" [class.compare-row__you--muted]="metric().you == null">
          {{ metric().you != null ? 'You: ' + formattedYou() : 'Not enough data yet' }}
        </span>
      </div>

      <div class="compare-row__track" aria-hidden="true">
        <div class="compare-row__zero-line" *ngIf="zeroLine() != null" [style.left.%]="zeroLine()"></div>
        <div
          class="compare-row__band compare-row__band--with"
          *ngIf="withLoyaltyBand() as band"
          [style.left.%]="band.left"
          [style.width.%]="band.width"
        ></div>
        <div
          class="compare-row__band compare-row__band--without"
          *ngIf="withoutLoyaltyBand() as band"
          [style.left.%]="band.left"
          [style.width.%]="band.width"
        ></div>
        <div class="compare-row__you-marker" *ngIf="metric().you != null" [style.left.%]="youPercent()">
          <div class="compare-row__you-line"></div>
          <div class="compare-row__you-dot"></div>
        </div>
      </div>

      <p class="compare-row__caption" *ngIf="typicalRange()">{{ typicalRange() }}</p>

      <span class="visually-hidden">{{ ariaSummary() }}</span>
    </div>
  `,
  styles: [
    `
      .compare-row {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .compare-row__top {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.75rem;
        font-size: 15px;
      }
      .compare-row__you {
        font-weight: 600;
      }
      .compare-row__you--muted {
        font-weight: 400;
        color: #5a5f66;
      }
      .compare-row__track {
        position: relative;
        height: 40px;
        border-radius: 8px;
        background: #efece5;
      }
      .compare-row__zero-line {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 1px;
        background: #c9c5bc;
      }
      .compare-row__band {
        position: absolute;
        height: 12px;
        min-width: 3px;
        border-radius: 999px;
      }
      .compare-row__band--with {
        top: 8px;
        background: rgba(31, 95, 168, 0.28);
      }
      .compare-row__band--without {
        bottom: 8px;
        background: rgba(184, 90, 28, 0.28);
      }
      .compare-row__you-marker {
        position: absolute;
        top: -4px;
        bottom: -4px;
        transform: translateX(-50%);
      }
      .compare-row__you-line {
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 2px;
        transform: translateX(-50%);
        background: #1b1d1f;
      }
      .compare-row__you-dot {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 16px;
        height: 16px;
        transform: translate(-50%, -50%);
        border-radius: 50%;
        background: #1b1d1f;
        border: 3px solid #fff;
      }
      .compare-row__caption {
        margin: 0;
        font-size: 12px;
        color: #5a5f66;
      }
    `,
  ],
})
export class CompareMetricRowComponent {
  readonly metric = input.required<CompareMetric>();

  protected readonly domain = computed(() => computeDomain(gatherDomainValues(this.metric()), this.metric().format));

  protected readonly zeroLine = computed(() =>
    zeroLinePercent(this.domain().min, this.domain().max, this.metric().format)
  );

  protected readonly youPercent = computed(() => {
    const you = this.metric().you;
    return you == null ? null : valueToPercent(you, this.domain().min, this.domain().max);
  });

  protected readonly formattedYou = computed(() => {
    const you = this.metric().you;
    return you == null ? null : formatMetricValue(you, this.metric().format, this.metric().decimals);
  });

  protected readonly withLoyaltyBand = computed(() => {
    const tier = this.metric().withLoyalty;
    return tier ? tierBandPercents(tier, this.domain().min, this.domain().max) : null;
  });

  protected readonly withoutLoyaltyBand = computed(() => {
    const tier = this.metric().withoutLoyalty;
    return tier ? tierBandPercents(tier, this.domain().min, this.domain().max) : null;
  });

  /** "Typical range" = each tier's middle half (p25–p75) — already outlier-resistant, so there's no separate min/max or median to show. */
  protected readonly typicalRange = computed(() => {
    const metric = this.metric();
    const parts: string[] = [];
    if (metric.withLoyalty) {
      parts.push(`with loyalty ${this.rangeDash(metric.withLoyalty.p25, metric.withLoyalty.p75)}`);
    }
    if (metric.withoutLoyalty) {
      parts.push(`without loyalty ${this.rangeDash(metric.withoutLoyalty.p25, metric.withoutLoyalty.p75)}`);
    }
    return parts.length ? `Typical range: ${parts.join(', ')}` : '';
  });

  protected readonly ariaSummary = computed(() => {
    const metric = this.metric();
    const sentences: string[] = [`${metric.label}:`];
    sentences.push(
      metric.you != null
        ? `you ${formatMetricValue(metric.you, metric.format, metric.decimals)}.`
        : 'not enough of your data yet.'
    );
    const parts: string[] = [];
    if (metric.withLoyalty) {
      parts.push(`with loyalty ${this.rangeTo(metric.withLoyalty.p25, metric.withLoyalty.p75)}`);
    }
    if (metric.withoutLoyalty) {
      parts.push(`without loyalty ${this.rangeTo(metric.withoutLoyalty.p25, metric.withoutLoyalty.p75)}`);
    }
    if (parts.length) {
      sentences.push(`Typical range for merchants ${parts.join(', and ')}.`);
    }
    return sentences.join(' ');
  });

  /** "$14.20–$17.60" — for the visual caption. */
  private rangeDash(p25: number, p75: number): string {
    const metric = this.metric();
    return `${formatMetricValue(p25, metric.format, metric.decimals)}–${formatMetricValue(
      p75,
      metric.format,
      metric.decimals
    )}`;
  }

  /** "$14.20 to $17.60" — reads more naturally for screen readers than an en dash. */
  private rangeTo(p25: number, p75: number): string {
    const metric = this.metric();
    return `${formatMetricValue(p25, metric.format, metric.decimals)} to ${formatMetricValue(
      p75,
      metric.format,
      metric.decimals
    )}`;
  }
}
